import api from '../../shared/services/api';

// Types
export interface AnnotationType {
  HIGHLIGHT: 'highlight';
  COMMENT: 'comment';
  ISSUE: 'issue';
  MISSING: 'missing';
  CRITICAL: 'critical';
  WORKFLOW: 'workflow';
  COMPLIANCE: 'compliance';
  EXTRACTION: 'extraction';
}

export interface AnnotationPriority {
  LOW: 'low';
  MEDIUM: 'medium';
  HIGH: 'high';
  CRITICAL: 'critical';
}

export interface Annotation {
  id: string;
  type: string;
  priority: string;
  field_name: string;
  field_value?: any;
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  page_number: number;
  message: string;
  suggestions: string[];
  created_by: string;
  created_at: string;
  resolved: boolean;
  resolved_by?: string;
  resolved_at?: string;
  replies: Reply[];
  metadata: Record<string, any>;
}

export interface Reply {
  id: string;
  user_id: string;
  user_name: string;
  message: string;
  timestamp: string;
}

export interface ComplianceIssue {
  issue_type: string;
  severity: string;
  field: string;
  message: string;
  regulation?: string;
  resolution?: string;
  detected_at: string;
}

export interface ComplianceReport {
  compliant: boolean;
  issues: ComplianceIssue[];
  critical_count: number;
  warning_count: number;
  info_count: number;
  checked_regulations: string[];
  timestamp: string;
}

export interface RoutingDecision {
  document_id: string;
  current_stage: string;
  next_stage: string;
  action: string;
  next_assignee: {
    role: string;
    level: string;
    specialization?: string;
  };
  priority: string;
  estimated_processing_time: number;
  recommendations: string[];
  confidence_score: number;
  timestamp: string;
}

export interface AnnotationStatistics {
  total_annotations: number;
  critical_count: number;
  missing_count: number;
  resolved_count: number;
  document_completeness: number;
}

export interface MagicMarkerResponse {
  document_id: string;
  annotations: Record<string, Annotation[]>;
  compliance_report?: ComplianceReport;
  routing_decision?: RoutingDecision;
  statistics: AnnotationStatistics;
  collaboration_enabled: boolean;
  session_id?: string;
}

export interface CollaborationSession {
  session_id: string;
  document_id: string;
  participants: Participant[];
  annotations: Record<string, any>;
  can_edit: boolean;
}

export interface Participant {
  user_id: string;
  user_name: string;
  session_id: string;
  joined_at: string;
  cursor_position?: { x: number; y: number };
  active: boolean;
}

export interface CollaborationEvent {
  id: string;
  user_id: string;
  user_name: string;
  action: string;
  annotation_id: string;
  timestamp: string;
  details: Record<string, any>;
}

// Request Types
export interface AnnotateDocumentRequest {
  document_id: string;
  document_type?: string;
  workflow_type?: string;
  auto_detect?: boolean;
  check_compliance?: boolean;
  enable_routing?: boolean;
  use_mistral_annotations?: boolean;
  extract_visual_elements?: boolean;
  fallback_to_legacy?: boolean;
}

export interface AddAnnotationRequest {
  type: string;
  priority: string;
  field_name: string;
  message: string;
  coordinates: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  page_number: number;
  suggestions?: string[];
  metadata?: Record<string, any>;
}

export interface ReplyToAnnotationRequest {
  annotation_id: string;
  message: string;
}

export interface ResolveAnnotationRequest {
  annotation_id: string;
  resolution_note?: string;
}

export interface CollaborationSessionRequest {
  document_id: string;
  enable_real_time?: boolean;
}

export interface ExportAnnotationsRequest {
  document_id: string;
  format?: 'json' | 'pdf' | 'xml';
  include_resolved?: boolean;
  include_compliance?: boolean;
}

// Service Class
class MagicMarkerService {
  /**
   * Annotate a document with AI-powered annotations
   */
  async annotateDocument(request: AnnotateDocumentRequest): Promise<MagicMarkerResponse> {
    const response = await api.post('/api/v1/magic-marker/annotate', request);
    return response.data;
  }

  /**
   * Start a collaboration session
   */
  async startCollaboration(request: CollaborationSessionRequest): Promise<CollaborationSession> {
    const response = await api.post('/api/v1/magic-marker/collaborate/start', request);
    return response.data;
  }

  /**
   * Add a collaborative annotation
   */
  async addCollaborativeAnnotation(
    documentId: string,
    annotation: AddAnnotationRequest
  ): Promise<{ success: boolean; annotation: Annotation }> {
    const response = await api.post('/api/v1/magic-marker/collaborate/annotation/add', {
      ...annotation,
      document_id: documentId,
    });
    return response.data;
  }

  /**
   * Reply to an annotation
   */
  async replyToAnnotation(
    documentId: string,
    request: ReplyToAnnotationRequest
  ): Promise<{ success: boolean; annotation: Annotation }> {
    const response = await api.post('/api/v1/magic-marker/collaborate/annotation/reply', {
      document_id: documentId,
      ...request,
    });
    return response.data;
  }

  /**
   * Resolve an annotation
   */
  async resolveAnnotation(
    documentId: string,
    request: ResolveAnnotationRequest
  ): Promise<{ success: boolean; annotation: Annotation }> {
    const response = await api.post('/api/v1/magic-marker/collaborate/annotation/resolve', {
      document_id: documentId,
      ...request,
    });
    return response.data;
  }

  /**
   * Get collaboration history
   */
  async getCollaborationHistory(
    documentId: string,
    limit: number = 50
  ): Promise<{ events: CollaborationEvent[]; total_events: number; participants: string[] }> {
    const response = await api.get(`/api/v1/magic-marker/collaborate/history/${documentId}`, {
      params: { limit },
    });
    return response.data;
  }

  /**
   * Batch annotate multiple documents
   */
  async batchAnnotate(
    documentIds: string[],
    options?: {
      document_type?: string;
      workflow_type?: string;
      auto_detect?: boolean;
      check_compliance?: boolean;
    }
  ): Promise<{
    processed: number;
    failed: number;
    results: MagicMarkerResponse[];
    errors: Array<{ document_id: string; error: string }>;
  }> {
    const response = await api.post('/api/v1/magic-marker/batch/annotate', {
      document_ids: documentIds,
      ...options,
    });
    return response.data;
  }

  /**
   * Export annotations
   */
  async exportAnnotations(request: ExportAnnotationsRequest): Promise<any> {
    const response = await api.post('/api/v1/magic-marker/export', request);
    return response.data;
  }

  /**
   * Import annotations
   */
  async importAnnotations(
    documentId: string,
    annotationsData: string,
    format: string = 'json',
    mergeExisting: boolean = true
  ): Promise<{ success: boolean; imported_count: number }> {
    const response = await api.post('/api/v1/magic-marker/import', {
      document_id: documentId,
      annotations_data: annotationsData,
      format,
      merge_existing: mergeExisting,
    });
    return response.data;
  }

  /**
   * Get annotation statistics for a document
   */
  async getStatistics(documentId: string): Promise<AnnotationStatistics> {
    const response = await api.get(`/api/v1/magic-marker/statistics/${documentId}`);
    return response.data;
  }

  /**
   * Search annotations
   */
  async searchAnnotations(
    documentId: string,
    query: string,
    filters?: {
      type?: string;
      priority?: string;
      resolved?: boolean;
      created_by?: string;
    }
  ): Promise<Annotation[]> {
    const response = await api.get(`/api/v1/magic-marker/search/${documentId}`, {
      params: { query, ...filters },
    });
    return response.data;
  }

  /**
   * Get compliance report for a document
   */
  async getComplianceReport(documentId: string): Promise<ComplianceReport> {
    const response = await api.get(`/api/v1/magic-marker/compliance/${documentId}`);
    return response.data;
  }

  /**
   * Get routing decision for a document
   */
  async getRoutingDecision(documentId: string): Promise<RoutingDecision> {
    const response = await api.get(`/api/v1/magic-marker/routing/${documentId}`);
    return response.data;
  }

  /**
   * Update routing decision
   */
  async updateRoutingDecision(
    documentId: string,
    action: string,
    notes?: string
  ): Promise<{ success: boolean; new_stage: string }> {
    const response = await api.post(`/api/v1/magic-marker/routing/${documentId}/update`, {
      action,
      notes,
    });
    return response.data;
  }

  /**
   * Subscribe to real-time updates (WebSocket)
   */
  subscribeToUpdates(
    documentId: string,
    callbacks: {
      onAnnotationAdded?: (annotation: Annotation) => void;
      onAnnotationUpdated?: (annotation: Annotation) => void;
      onAnnotationResolved?: (annotation: Annotation) => void;
      onParticipantJoined?: (participant: Participant) => void;
      onParticipantLeft?: (participant: Participant) => void;
    }
  ): () => void {
    // This would implement WebSocket connection
    // For now, returning a no-op unsubscribe function
    console.log(`Subscribing to updates for document ${documentId}`);
    
    // Return unsubscribe function
    return () => {
      console.log(`Unsubscribing from updates for document ${documentId}`);
    };
  }
}

// Export singleton instance
export const magicMarkerService = new MagicMarkerService();
export default magicMarkerService;